import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, AlertIcon, Checkbox, Badge, Box, Button, Divider, Flex, FormControl, FormLabel, HStack, Icon,
    IconButton, Image, Input, Link as ChakraLink, Modal, ModalBody, ModalCloseButton, ModalContent,
    ModalFooter, ModalHeader, ModalOverlay, Select, SimpleGrid, Skeleton, Spinner, Text, Tooltip, VStack,
    useBreakpointValue, useToast,
} from '@chakra-ui/react';
import { ExternalLinkIcon, RepeatIcon, WarningTwoIcon } from '@chakra-ui/icons';
import {
    FaMotorcycle, FaCarSide, FaTruck, FaShuttleVan, FaMapMarkerAlt, FaUser, FaPhone,
    FaClipboardCheck, FaTimesCircle, FaCheckCircle, FaBoxOpen, FaPlus,
} from 'react-icons/fa';
import {
    cancelarOrdemLalamove, cotarLalamove, cotarModalidadeLalamove, criarOrdemLalamove, descartarPendenteLalamove,
    aumentarGorjetaAgoraLalamove, pausarAumentoGorjetaLalamove, trocarMotoristaLalamove,
    getCorridaLalamove, getLalamoveServicos, getOrdemLalamove, prepararLalamove,
} from '../../services/api';
import BuscaVenda from '../BuscaVenda';
import EscolhaPrioridade from '../GorjetaLalamove/EscolhaPrioridade';
import PainelGorjeta from '../GorjetaLalamove/PainelGorjeta';
import { normalizar as normalizarGorjeta, valor as valorGorjeta, valorTeto as tetoGorjeta } from '../GorjetaLalamove/regras';
import { formatarTelefone, soDigitosTelefone, telefoneValido } from './telefone';
import SeletorEnderecoMapa from '../SeletorEnderecoMapa';
import {
    doCadastro, impedimentos as impedimentosDoDestino, linhaLalamove, observacaoDoMotorista, paraPayload,
} from '../SeletorEnderecoMapa/endereco';

const ICONE_SERVICO = {
    LALAGO: FaMotorcycle,
    LALAPRO: FaMotorcycle,
    HATCHBACK: FaCarSide,
    CAR: FaCarSide,
    UV_FIORINO: FaShuttleVan,
    VAN: FaShuttleVan,
    TRUCK330: FaTruck,
    TRUCK3_5T: FaTruck,
};

const CORES_STATUS = {
    PENDENTE: 'gray',
    ASSIGNING_DRIVER: 'yellow',
    ON_GOING: 'blue',
    PICKED_UP: 'purple',
    COMPLETED: 'green',
    CANCELED: 'red',
    EXPIRED: 'red',
    REJECTED: 'red',
};

const brl = (valor) => `R$ ${Number(valor || 0).toFixed(2).replace('.', ',')}`;

const corDaMargem = (margem, freteCobrado) => {
    if (margem === null || margem === undefined) return 'gray';
    if (margem >= 0) return 'green';
    if (freteCobrado > 0 && margem / freteCobrado >= -0.1) return 'yellow';
    return 'red';
};

const mapaUrl = (lat, lng) => {
    const d = 0.004;
    const bbox = [Number(lng) - d, Number(lat) - d, Number(lng) + d, Number(lat) + d].join('%2C');
    return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lng}`;
};

/**
 * Solicitacao de corrida Lalamove.
 *
 * Dois pontos de entrada, um fluxo so:
 *  - Gestao de Despacho abre com `vendaId` e vai direto para a revisao;
 *  - a pagina /lalamove abre com `avulsa`, e o operador escolhe primeiro se
 *    parte de uma venda ou de nada.
 *
 * Dali para a frente — endereco, mapa, cotacao, adicionais, confirmacao — e
 * exatamente o mesmo caminho. Nao existe um segundo fluxo a manter.
 */
export default function ModalSolicitacaoLalamove({ isOpen, onClose, vendaId, avulsa = false, onOrdemCriada, onColetaCompra = null }) {
    const toast = useToast();
    const compacto = useBreakpointValue({ base: true, md: false });

    const [etapa, setEtapa] = useState('carregando');
    const [erro, setErro] = useState(null);
    const [dados, setDados] = useState(null);
    const [ordem, setOrdem] = useState(null);

    const [destino, setDestino] = useState(() => doCadastro({}));
    const [observacao, setObservacao] = useState('');
    const [vendaVinculada, setVendaVinculada] = useState(null);
    const [destinatario, setDestinatario] = useState({ nome: '', telefone: '' });
    const [trocandoMotorista, setTrocandoMotorista] = useState(false);
    const [motivoTroca, setMotivoTroca] = useState('DRIVER_LATE');
    const [confirmarTroca, setConfirmarTroca] = useState(false);

    const [cotacoes, setCotacoes] = useState([]);
    const [falhas, setFalhas] = useState({});
    const [escolhida, setEscolhida] = useState(null);
    const [cotando, setCotando] = useState(false);
    const [confirmando, setConfirmando] = useState(false);
    const [cancelando, setCancelando] = useState(false);
    const [confirmarNaLalamove, setConfirmarNaLalamove] = useState(false);
    const [adicionaisPorServico, setAdicionaisPorServico] = useState({});
    const [adicionais, setAdicionais] = useState([]);
    const [recotando, setRecotando] = useState(false);
    const [totalSemAdicionais, setTotalSemAdicionais] = useState(null);
    const [descartando, setDescartando] = useState(false);
    const [restante, setRestante] = useState(0);
    const [prioridade, setPrioridade] = useState(false);
    const [regrasGorjeta, setRegrasGorjeta] = useState(null);
    const [pausandoGorjeta, setPausandoGorjeta] = useState(false);

    const expiraEm = useRef(null);
    const corpoRef = useRef(null);

    useEffect(() => {
        if (corpoRef.current) corpoRef.current.scrollTop = 0;
    }, [etapa]);

    /**
     * Altura do modal no celular, medida pelo visualViewport.
     *
     * O tema do Chakra fixa `h: 100vh` no size="full". Com o teclado aberto,
     * 100vh deixa de descrever o que se ve: o modal ficava do tamanho errado e
     * nao voltava quando o teclado fechava — sobrava a pagina aparecendo por
     * baixo, com o modal congelado no meio da tela.
     *
     * visualViewport e a unica medida que acompanha o teclado abrindo e
     * fechando. Vira variavel CSS para o Chakra usar sem re-render a cada
     * pixel. Navegador sem visualViewport cai em 100dvh, e depois 100vh.
     */
    useEffect(() => {
        if (!isOpen || !compacto) return undefined;

        const vv = window.visualViewport;
        const raiz = document.documentElement;

        const medir = () => {
            const altura = vv ? vv.height : window.innerHeight;
            raiz.style.setProperty('--taga-modal-h', `${Math.round(altura)}px`);
        };

        medir();
        vv?.addEventListener('resize', medir);
        vv?.addEventListener('scroll', medir);
        window.addEventListener('orientationchange', medir);

        return () => {
            vv?.removeEventListener('resize', medir);
            vv?.removeEventListener('scroll', medir);
            window.removeEventListener('orientationchange', medir);
            raiz.style.removeProperty('--taga-modal-h');
        };
    }, [isOpen, compacto]);

    const alturaModal = 'var(--taga-modal-h, 100dvh)';

    const reiniciar = useCallback(() => {
        setEtapa('carregando');
        setErro(null);
        setDados(null);
        setOrdem(null);
        setCotacoes([]);
        setFalhas({});
        setEscolhida(null);
        setObservacao('');
        setDestino(doCadastro({}));
        setVendaVinculada(null);
        setDestinatario({ nome: '', telefone: '' });
        setPrioridade(false);
        setRegrasGorjeta(null);
        expiraEm.current = null;
    }, []);

    const carregar = useCallback(async (idDaVenda = vendaId) => {
        try {
            setEtapa('carregando');
            setErro(null);
            const res = await prepararLalamove(idDaVenda);

            if (res.ja_solicitada) {
                setOrdem(res.ordem);
                setConfirmarNaLalamove(!!res.confirmar_na_lalamove);
                setEtapa('criada');
                return;
            }

            setDados(res);
            // O destino ja vem montado do backend: texto do cadastro da venda
            // (onde o numero e confiavel) e, quando houve tentativa anterior,
            // o ponto que alguem ja conferiu no mapa.
            setDestino(doCadastro(res.destino || res.endereco || {}));
            setDestinatario({
                nome: res.cliente?.nome || '',
                telefone: soDigitosTelefone(res.cliente?.telefone || ''),
            });
            setEtapa('revisao');

            getLalamoveServicos()
                .then((s) => {
                    const mapa = {};
                    (s.servicos || []).forEach((servico) => {
                        mapa[servico.service_type] = servico.adicionais || [];
                    });
                    setAdicionaisPorServico(mapa);
                })
                .catch(() => setAdicionaisPorServico({}));
        } catch (e) {
            setErro(e.response?.data?.error || 'Não foi possível preparar a solicitação.');
            setEtapa('erro');
        }
    }, [vendaId]);

    useEffect(() => {
        if (!isOpen) return;

        reiniciar();

        // Com venda, vai direto para a revisao, como sempre foi. Sem venda, o
        // operador escolhe primeiro de onde a corrida parte.
        if (vendaId) carregar(vendaId);
        else setEtapa('origem');
    }, [isOpen, vendaId, carregar, reiniciar]);

    useEffect(() => {
        if (etapa !== 'cotacoes' || !expiraEm.current) return undefined;

        const tick = () => {
            const segundos = Math.max(0, Math.floor((new Date(expiraEm.current).getTime() - Date.now()) / 1000));
            setRestante(segundos);
        };

        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [etapa]);

    const expirada = etapa === 'cotacoes' && restante <= 0;

    /**
     * A venda desta corrida: a do despacho, a que veio de `preparar`, ou a que
     * o operador escolheu na busca. A ultima importa porque, quando a venda ja
     * tem corrida, `preparar` responde direto com a ordem e nao preenche
     * `dados` — sem ela o modal perderia o vinculo na tela e no cancelamento.
     */
    const vendaAtual = vendaId ?? dados?.venda?.id ?? vendaVinculada?.id ?? null;
    const semVenda = !vendaAtual;

    /** Como apontar a corrida depois de criada: por venda ou pelo id da ordem. */
    const alvo = { vendaId: vendaAtual, ordemId: ordem?.id ?? null };

    /**
     * Volta ao passo de escolha da origem.
     *
     * So existe na corrida avulsa. Sem isto, escolher a venda errada — ou cair
     * no erro de uma venda que nao serve — deixava o operador preso: nao dava
     * para trocar de venda nem passar para o preenchimento manual sem fechar e
     * reabrir o modal. No despacho nao ha para onde voltar, e o botao nao
     * aparece.
     */
    const voltarParaOrigem = () => {
        setDados(null);
        setErro(null);
        setOrdem(null);
        setCotacoes([]);
        setEscolhida(null);
        setVendaVinculada(null);
        setDestino(doCadastro({}));
        setDestinatario({ nome: '', telefone: '' });
        setEtapa('origem');
    };

    const destinatarioValido = !semVenda
        || (destinatario.nome.trim().length > 1 && telefoneValido(destinatario.telefone));

    /**
     * O que ainda impede esta corrida de sair.
     *
     * A mesma conta roda no servidor (App\Services\EnderecoEntrega). Aqui ela
     * serve para o operador nao descobrir o problema depois de cotar; la ela
     * serve porque a corrida nao pode depender de a tela ter feito a parte dela.
     */
    const impedimentos = useMemo(
        () => impedimentosDoDestino(destino, dados?.endereco || null),
        [destino, dados],
    );

    const enderecoPronto = impedimentos.length === 0;

    /** Todo request que fala de entrega leva os campos separados. */
    const enderecoNoPayload = useCallback(
        (base) => ({
            ...base,
            venda_id: vendaAtual ?? undefined,
            destino: paraPayload(destino),
        }),
        [destino, vendaAtual],
    );

    const cotar = async () => {
        setAdicionais([]);
        setTotalSemAdicionais(null);

        if (impedimentos.length > 0) {
            toast({ title: 'Endereço incompleto', description: impedimentos[0], status: 'warning', duration: 7000, isClosable: true });
            return;
        }

        setCotando(true);
        setErro(null);
        try {
            const res = await cotarLalamove(enderecoNoPayload({}));
            setCotacoes(res.cotacoes || []);
            setFalhas(res.falhas || {});
            // Backend antigo nao manda as regras: o seletor de modo some e a
            // corrida sai como sempre saiu.
            setRegrasGorjeta(res.aumento_gorjeta ? normalizarGorjeta(res.aumento_gorjeta) : null);
            expiraEm.current = res.cotacoes?.[0]?.expira_em || null;

            const sugerida = (res.cotacoes || []).find((c) => c.service_type === res.service_type_checkout);
            setEscolhida(sugerida || res.cotacoes?.[0] || null);
            setEtapa('cotacoes');
        } catch (e) {
            const dadosErro = e.response?.data || {};
            const msg = dadosErro.error || 'Não foi possível cotar na Lalamove.';

            if (dadosErro.endereco_invalido) {
                setEtapa('revisao');
                toast({ title: 'Endereço recusado', description: msg, status: 'warning', duration: 9000, isClosable: true });
                return;
            }

            setErro(msg);
            toast({ title: 'Falha na cotação', description: msg, status: 'error', isClosable: true });
        } finally {
            setCotando(false);
        }
    };

    const confirmar = async () => {
        if (!escolhida) return;

        setConfirmando(true);
        try {
            const res = await criarOrdemLalamove(enderecoNoPayload({
                quotation_id: escolhida.quotation_id,
                service_type: escolhida.service_type,
                preco_confirmado: escolhida.total,
                observacao: observacao || undefined,
                adicionais: adicionais.length ? adicionais : undefined,
                destinatario: semVenda
                    ? { nome: destinatario.nome.trim(), telefone: destinatario.telefone.trim() }
                    : undefined,
                prioridade: regrasGorjeta ? prioridade : undefined,
            }));
            setOrdem(res.ordem);
            setEtapa('criada');

            // A gorjeta inicial sai depois da corrida criada. Se a Lalamove
            // recusou, a corrida existe e o operador precisa saber que o
            // acrescimo nao entrou (a rotina tenta de novo sozinha).
            const g = res.ordem?.gorjeta;
            const inicialFalhou = g && g.erro && prioridade;
            toast({
                title: prioridade ? 'Corrida solicitada com prioridade' : 'Corrida solicitada',
                description: inicialFalhou
                    ? `A corrida foi criada, mas a gorjeta inicial não entrou: ${g.erro}`
                    : (prioridade && g?.atual > 0
                        ? `Gorjeta de ${brl(g.atual)} já enviada. A Lalamove está procurando motorista.`
                        : 'A Lalamove já está procurando um motorista.'),
                status: inicialFalhou ? 'warning' : 'success',
                duration: inicialFalhou ? 12000 : 6000,
                isClosable: true,
            });
            if (onOrdemCriada) onOrdemCriada(res.ordem);
        } catch (e) {
            const dadosErro = e.response?.data || {};

            if (dadosErro.recotar && dadosErro.cotacao) {
                setEscolhida(dadosErro.cotacao);
                setCotacoes((antigas) => antigas.map((c) => (c.service_type === dadosErro.cotacao.service_type ? dadosErro.cotacao : c)));
                expiraEm.current = dadosErro.cotacao.expira_em;
                setEtapa('cotacoes');
                toast({ title: 'Cotação expirada', description: dadosErro.error, status: 'warning', duration: 8000, isClosable: true });
                return;
            }

            if (dadosErro.preco_mudou) {
                setEscolhida((c) => (c ? { ...c, total: dadosErro.preco_atual } : c));
                setEtapa('cotacoes');
                toast({ title: 'O preço mudou', description: dadosErro.error, status: 'warning', duration: 10000, isClosable: true });
                return;
            }

            if (dadosErro.endereco_invalido) {
                setEtapa('revisao');
                toast({
                    title: 'Endereço recusado',
                    description: dadosErro.error,
                    status: 'warning',
                    duration: null,
                    isClosable: true,
                });
                return;
            }

            if (dadosErro.pendente) {
                toast({
                    title: 'Confira na Lalamove antes de pedir de novo',
                    description: dadosErro.error,
                    status: 'warning',
                    duration: null,
                    isClosable: true,
                });
                carregar();
                return;
            }

            toast({ title: 'Não foi possível criar a corrida', description: dadosErro.error || 'Erro inesperado.', status: 'error', duration: 9000, isClosable: true });
            if (dadosErro.ordem) {
                setOrdem(dadosErro.ordem);
                setEtapa('criada');
            }
        } finally {
            setConfirmando(false);
        }
    };

    const [atualizando, setAtualizando] = useState(false);

    const atualizarOrdem = async () => {
        setAtualizando(true);
        try {
            const res = alvo.vendaId
                ? await getOrdemLalamove(alvo.vendaId, true)
                : await getCorridaLalamove(alvo.ordemId, true);
            if (res.ordem) setOrdem(res.ordem);
        } catch (e) {
            toast({ title: 'Falha ao atualizar', description: e.response?.data?.error, status: 'error', isClosable: true });
        } finally {
            setAtualizando(false);
        }
    };

    /**
     * Enquanto a Lalamove procura motorista, a gorjeta e o status mudam sem
     * ninguem clicar. A tela le do banco (sem chamar a Lalamove) a cada 20s,
     * e para quando a aba fica escondida - o servidor ja faz o trabalho.
     */
    const procurando = etapa === 'criada' && ['ASSIGNING_DRIVER', 'PENDENTE'].includes(ordem?.status) && !confirmarNaLalamove;
    const alvoVenda = alvo.vendaId;
    const alvoOrdem = alvo.ordemId;

    useEffect(() => {
        if (!isOpen || !procurando) return undefined;

        const ler = async () => {
            if (document.hidden) return;
            try {
                const res = alvoVenda ? await getOrdemLalamove(alvoVenda) : await getCorridaLalamove(alvoOrdem);
                if (res?.ordem) setOrdem(res.ordem);
            } catch (e) {
                // Silencioso: o proximo ciclo tenta de novo e o botao de
                // atualizar continua ai.
            }
        };

        const id = setInterval(ler, 20000);
        document.addEventListener('visibilitychange', ler);
        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', ler);
        };
    }, [isOpen, procurando, alvoVenda, alvoOrdem]);

    /** "Aumentar agora": devolve { ok, erro } para o dialogo de confirmacao. */
    const aumentarGorjetaAgora = async () => {
        try {
            const res = await aumentarGorjetaAgoraLalamove(alvo);
            if (res?.ordem) setOrdem(res.ordem);
            const g = res?.ordem?.gorjeta;
            toast({
                title: `Gorjeta aumentada para ${brl(g?.atual)}`,
                description: g?.proximo_valor != null
                    ? `Se ninguém aceitar, sobe para ${brl(g.proximo_valor)} no próximo intervalo.`
                    : 'Chegou no teto desta corrida.',
                status: 'success',
                duration: 6000,
                isClosable: true,
            });
            return { ok: true };
        } catch (e) {
            const d = e.response?.data || {};
            if (d.ordem) setOrdem(d.ordem);
            return { ok: false, erro: d.error || 'Falha de comunicação. Tente de novo.' };
        }
    };

    const pausarGorjeta = async (pausar, desfazendo = false) => {
        setPausandoGorjeta(true);
        try {
            const res = await pausarAumentoGorjetaLalamove(alvo, pausar);
            if (res?.ordem) setOrdem(res.ordem);

            const atual = res?.ordem?.gorjeta?.atual ?? 0;
            const idToast = `gorjeta-${alvo.ordemId || alvo.vendaId}`;
            toast.close(idToast);
            if (!desfazendo) {
                toast({
                    id: idToast,
                    position: 'bottom',
                    duration: 8000,
                    isClosable: true,
                    status: pausar ? 'info' : 'success',
                    title: pausar ? 'Aumentos parados' : 'Aumentos retomados',
                    description: (
                        <HStack justify="space-between" spacing={3} w="100%">
                            <Text fontSize="sm">
                                {pausar ? `A gorjeta fica em ${brl(atual)}.` : `Gorjeta agora: ${brl(atual)}.`}
                            </Text>
                            {pausar && (
                                <Button
                                    size="xs"
                                    variant="outline"
                                    colorScheme="whiteAlpha"
                                    color="white"
                                    flexShrink={0}
                                    onClick={() => { toast.close(idToast); pausarGorjeta(false, true); }}
                                >
                                    Desfazer
                                </Button>
                            )}
                        </HStack>
                    ),
                });
            }
        } catch (e) {
            const d = e.response?.data || {};
            if (d.ordem) setOrdem(d.ordem);
            toast({
                title: pausar ? 'Não foi possível parar os aumentos' : 'Não foi possível retomar os aumentos',
                description: d.error || 'Falha de comunicação. Tente de novo.',
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        } finally {
            setPausandoGorjeta(false);
        }
    };

    const trocarMotorista = async () => {
        setTrocandoMotorista(true);
        try {
            const res = await trocarMotoristaLalamove(alvo, motivoTroca);
            if (res?.success) {
                setOrdem(res.ordem);
                setConfirmarTroca(false);
                toast({
                    title: 'Motorista dispensado',
                    description: res.mensagem || 'A Lalamove ja esta procurando outro.',
                    status: 'success',
                    duration: 6000,
                    isClosable: true,
                });
            } else {
                toast({ title: 'Nao deu para trocar', description: res?.error || 'A Lalamove recusou.', status: 'error', duration: 9000, isClosable: true });
            }
        } catch (e) {
            toast({ title: 'Nao deu para trocar', description: e.response?.data?.error || 'Falha de comunicacao.', status: 'error', duration: 9000, isClosable: true });
        } finally {
            setTrocandoMotorista(false);
        }
    };

    const cancelar = async () => {
        setCancelando(true);
        try {
            const res = await cancelarOrdemLalamove(alvo, 'Cancelada pelo operador no TAGA Admin');
            setOrdem(res.ordem);
            toast({ title: 'Corrida cancelada', status: 'success', isClosable: true });
        } catch (e) {
            const d = e.response?.data || {};
            if (d.ordem) setOrdem(d.ordem);
            toast({ title: 'Não foi possível cancelar', description: d.error || 'Erro inesperado.', status: 'error', duration: 9000, isClosable: true });
        } finally {
            setCancelando(false);
        }
    };

    const pinSelecionado = useMemo(() => {
        const destino = escolhida?.stops?.[1];
        if (!destino?.lat || !destino?.lng) return null;
        return { lat: destino.lat, lng: destino.lng };
    }, [escolhida]);

    const nfePendente = dados && dados.nfe_emitida === false;

    const cabecalho = (
        <ModalHeader borderBottomWidth="1px" py={3} pr={12}>
            <HStack spacing={2} wrap="wrap">
                <Image src="/lalamove_logo.webp" alt="Lalamove" h="16px" w="auto" objectFit="contain" />
                <Text fontSize="md" fontWeight="bold">Solicitar corrida</Text>
                {vendaAtual && (
                    <Badge colorScheme="purple" variant="subtle">Venda #{vendaAtual}</Badge>
                )}
                {dados && semVenda && (
                    <Badge colorScheme="orange" variant="subtle">Avulsa</Badge>
                )}
                {ordem?.order_id && (
                    <Badge colorScheme={CORES_STATUS[ordem.status] || 'gray'}>{ordem.status_rotulo}</Badge>
                )}
            </HStack>
        </ModalHeader>
    );

    /**
     * Passo de entrada da corrida avulsa: a corrida parte de uma venda ou de
     * nada. As duas saidas caem no mesmo `revisao` de sempre.
     */
    const renderOrigem = () => (
        <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.600">De onde parte esta corrida?</Text>

            <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3}>
                <HStack spacing={2} mb={2}>
                    <Icon as={FaBoxOpen} color="purple.500" boxSize={3} />
                    <Text fontSize="sm" fontWeight="bold">Carregar a partir de uma venda</Text>
                </HStack>
                <Text fontSize="xs" color="gray.500" mb={2}>
                    Traz endereço, cliente e frete. Fica vinculada à venda.
                </Text>

                <BuscaVenda
                    ativa={etapa === 'origem'}
                    escolhida={vendaVinculada}
                    onEscolher={(v) => { setVendaVinculada(v); carregar(v.id); }}
                    onLimpar={() => setVendaVinculada(null)}
                />
            </Box>

            <HStack>
                <Divider />
                <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">ou</Text>
                <Divider />
            </HStack>

            <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3}>
                <HStack spacing={2} mb={2}>
                    <Icon as={FaMapMarkerAlt} color="orange.500" boxSize={3} />
                    <Text fontSize="sm" fontWeight="bold">Criar corrida sem venda</Text>
                </HStack>
                <Text fontSize="xs" color="gray.500" mb={3}>
                    Você informa endereço e destinatário. Sem venda e sem NFe.
                </Text>
                <Button
                    size="sm"
                    colorScheme="orange"
                    variant="outline"
                    onClick={() => { setVendaVinculada(null); carregar(null); }}
                >
                    Preencher à mão
                </Button>
            </Box>

            {/* Coleta de compra no fornecedor (#2307): so na pagina da Lalamove. */}
            {onColetaCompra && (
                <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3}>
                    <HStack spacing={2} mb={2}>
                        <Icon as={FaBoxOpen} color="teal.500" boxSize={3} />
                        <Text fontSize="sm" fontWeight="bold">Coletar compra no fornecedor</Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mb={3}>
                        Busca listas de compras e pedidos de furo de um fornecedor e traz para a loja.
                    </Text>
                    <Button size="sm" colorScheme="teal" variant="outline" onClick={onColetaCompra}>
                        Escolher fornecedor
                    </Button>
                </Box>
            )}
        </VStack>
    );

    const renderRevisao = () => (
        <VStack align="stretch" spacing={4}>
            {nfePendente && (
                <Alert status="warning" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    NFe ainda não emitida. Você pode cotar para ver os preços, mas só dá para confirmar a corrida depois de emitir a nota.
                </Alert>
            )}

            <Box bg="gray.50" borderWidth="1px" borderRadius="md" p={3}>
                <HStack justify="space-between" wrap="wrap" spacing={3}>
                    <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Cliente pagou de frete</Text>
                        <Text fontSize="lg" fontWeight="bold" color="gray.800">{brl(dados?.venda?.frete_cobrado)}</Text>
                    </VStack>
                    <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Escolha no checkout</Text>
                        <Text fontSize="sm" color="gray.700">{dados?.venda?.modo_envio_checkout || '-'}</Text>
                    </VStack>
                    <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Cliente</Text>
                        <Text fontSize="sm" color="gray.700">{dados?.cliente?.nome || '-'}</Text>
                    </VStack>
                </HStack>
            </Box>

            <Box>
                <HStack spacing={2} mb={2}>
                    <Icon as={FaMapMarkerAlt} color="orange.500" boxSize={3} />
                    <Text fontSize="sm" fontWeight="bold">Endereço de entrega</Text>
                </HStack>
                {/* destinoInicial e o estado vivo do modal, nao o que veio do
                    servidor: o seletor desmonta ao sair da revisao e remonta no
                    "Voltar", e semear com a resposta original descartava tudo
                    que o operador tinha corrigido. */}
                <SeletorEnderecoMapa
                    aberto={isOpen}
                    compacto={compacto}
                    alturaMapa={compacto ? 200 : 260}
                    destinoInicial={destino}
                    cadastro={dados?.endereco || null}
                    onChange={setDestino}
                />
            </Box>

            {semVenda && (
                <Box borderWidth="1px" borderColor="orange.200" bg="orange.50" borderRadius="md" p={3}>
                    <HStack spacing={2} mb={2}>
                        <Icon as={FaUser} color="orange.500" boxSize={3} />
                        <Text fontSize="sm" fontWeight="bold">Quem vai receber</Text>
                    </HStack>
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                        <FormControl isRequired>
                            <FormLabel htmlFor="dest-nome" fontSize="xs" color="gray.600" mb={1}>Nome</FormLabel>
                            <Input
                                id="dest-nome"
                                size={compacto ? 'md' : 'sm'}
                                bg="white"
                                value={destinatario.nome}
                                onChange={(e) => setDestinatario((d) => ({ ...d, nome: e.target.value }))}
                                placeholder="Oficina do Ze"
                                maxLength={60}
                            />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel htmlFor="dest-tel" fontSize="xs" color="gray.600" mb={1}>Telefone com DDD</FormLabel>
                            <Input
                                id="dest-tel"
                                size={compacto ? 'md' : 'sm'}
                                bg="white"
                                inputMode="tel"
                                value={formatarTelefone(destinatario.telefone)}
                                onChange={(e) => setDestinatario((d) => ({ ...d, telefone: soDigitosTelefone(e.target.value) }))}
                                placeholder="(11) 98888-7777"
                                maxLength={15}
                            />
                        </FormControl>
                    </SimpleGrid>
                </Box>
            )}

            <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" mb={1}>Observação para o motoboy (opcional)</FormLabel>
                <Input size="sm" bg="white" value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Ex.: entregar na portaria, falar com o Sr. João" />
            </FormControl>

            {dados?.itens?.length > 0 && (
                <Box>
                    <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>Itens</Text>
                    <VStack align="stretch" spacing={1}>
                        {dados.itens.map((item, i) => (
                            <HStack key={i} spacing={2} fontSize="xs" color="gray.700">
                                <Icon as={FaBoxOpen} color="gray.400" boxSize={3} />
                                <Text noOfLines={1}>{item.quantidade}x {item.nome}</Text>
                            </HStack>
                        ))}
                    </VStack>
                </Box>
            )}
        </VStack>
    );

    const adicionaisDisponiveis = adicionaisPorServico[escolhida?.service_type] || [];

    const gruposDeAdicionais = adicionaisDisponiveis.reduce((acc, a) => {
        const chave = a.grupo || '_avulsos';
        (acc[chave] = acc[chave] || []).push(a);
        return acc;
    }, {});

    const aplicarAdicionais = async (novos) => {
        const anteriores = adicionais;
        setAdicionais(novos);
        setRecotando(true);

        if (totalSemAdicionais === null && escolhida) {
            setTotalSemAdicionais(escolhida.total);
        }

        try {
            const res = await cotarModalidadeLalamove(enderecoNoPayload({
                service_type: escolhida.service_type,
                adicionais: novos,
            }));
            const nova = { ...res.cotacao, rotulo: escolhida.rotulo };

            setEscolhida(nova);
            setCotacoes((antigas) => antigas.map((c) => (c.service_type === nova.service_type ? nova : c)));
            expiraEm.current = nova.expira_em;
        } catch (e) {
            setAdicionais(anteriores);
            toast({
                title: 'Não foi possível cotar com esse adicional',
                description: e.response?.data?.error || 'A Lalamove recusou a combinação.',
                status: 'error',
                duration: 7000,
                isClosable: true,
            });
        } finally {
            setRecotando(false);
        }
    };

    const escolherNoGrupo = (doGrupo, nome) => {
        const nomesDoGrupo = doGrupo.map((a) => a.nome);
        const semGrupo = adicionais.filter((a) => !nomesDoGrupo.includes(a));
        aplicarAdicionais(nome ? [...semGrupo, nome] : semGrupo);
    };

    const alternarAvulso = (nome) => {
        aplicarAdicionais(
            adicionais.includes(nome) ? adicionais.filter((a) => a !== nome) : [...adicionais, nome]
        );
    };

    const rotuloDoAdicional = (nome) => adicionaisDisponiveis.find((a) => a.nome === nome)?.rotulo || nome;

    const diferencaAdicionais = totalSemAdicionais !== null && escolhida
        ? Number((escolhida.total - totalSemAdicionais).toFixed(2))
        : 0;

    const renderAdicionais = () => {
        if (!escolhida || adicionaisDisponiveis.length === 0) return null;

        return (
            <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3} bg="gray.50">
                <Flex justify="space-between" align="center" gap={2} mb={2} wrap="wrap">
                    <HStack spacing={2}>
                        <Icon as={FaPlus} color="gray.400" boxSize={3} />
                        <Text fontSize="sm" fontWeight="bold" color="gray.700">
                            Adicionais de {escolhida.rotulo}
                        </Text>
                        {recotando && <Spinner size="xs" color="purple.500" />}
                    </HStack>
                    {adicionais.length > 0 && (
                        <Button size="xs" variant="ghost" onClick={() => aplicarAdicionais([])} isDisabled={recotando}>
                            limpar
                        </Button>
                    )}
                </Flex>

                <VStack align="stretch" spacing={3}>
                    {Object.entries(gruposDeAdicionais).map(([grupo, itens]) => (
                        <Box key={grupo}>
                            {grupo !== '_avulsos' && (
                                <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>
                                    {grupo}
                                </Text>
                            )}

                            {grupo === '_avulsos' ? (
                                <VStack align="stretch" spacing={1}>
                                    {itens.map((a) => (
                                        <Checkbox
                                            key={a.nome}
                                            size="sm"
                                            colorScheme="purple"
                                            isChecked={adicionais.includes(a.nome)}
                                            isDisabled={recotando}
                                            onChange={() => alternarAvulso(a.nome)}
                                        >
                                            <Text fontSize="sm">{a.rotulo}</Text>
                                        </Checkbox>
                                    ))}
                                </VStack>
                            ) : (
                                <Select
                                    size="sm"
                                    bg="white"
                                    borderRadius="lg"
                                    isDisabled={recotando}
                                    value={itens.find((a) => adicionais.includes(a.nome))?.nome || ''}
                                    onChange={(e) => escolherNoGrupo(itens, e.target.value)}
                                >
                                    <option value="">sem esse adicional</option>
                                    {itens.map((a) => (
                                        <option key={a.nome} value={a.nome}>{a.rotulo}</option>
                                    ))}
                                </Select>
                            )}
                        </Box>
                    ))}
                </VStack>

                {adicionais.length > 0 && diferencaAdicionais !== 0 && (
                    <Alert status="info" borderRadius="md" fontSize="xs" mt={3}>
                        <AlertIcon />
                        Os adicionais somaram <b>&nbsp;{brl(diferencaAdicionais)}&nbsp;</b> ao preço da corrida.
                    </Alert>
                )}
            </Box>
        );
    };

    const renderCotacoes = () => (
        <VStack align="stretch" spacing={3}>
            <HStack justify="space-between" wrap="wrap" spacing={2}>
                <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">Cliente pagou</Text>
                    <Text fontSize="sm" fontWeight="bold">{brl(dados?.venda?.frete_cobrado)}</Text>
                </HStack>
                <HStack spacing={2}>
                    <Badge colorScheme={expirada ? 'red' : restante < 60 ? 'orange' : 'green'} variant="subtle">
                        {expirada ? 'Cotação expirada' : `Expira em ${Math.floor(restante / 60)}:${String(restante % 60).padStart(2, '0')}`}
                    </Badge>
                    <Button size="xs" leftIcon={<RepeatIcon />} onClick={cotar} isLoading={cotando} variant="outline">
                        Recotar
                    </Button>
                </HStack>
            </HStack>

            <VStack
                align="stretch"
                spacing={2}
                maxH={compacto ? undefined : '38vh'}
                overflowY={compacto ? 'visible' : 'auto'}
                pr={1}
            >
                {cotacoes.map((c) => {
                    const ativa = escolhida?.service_type === c.service_type;
                    const cor = corDaMargem(c.margem, c.frete_cobrado);
                    const doCheckout = c.service_type === dados?.venda?.service_type_checkout;

                    return (
                        <Box
                            key={c.service_type}
                            borderWidth="2px"
                            borderColor={ativa ? 'purple.400' : 'gray.200'}
                            bg={ativa ? 'purple.50' : 'white'}
                            borderRadius="lg"
                            p={3}
                            cursor="pointer"
                            transition="all 0.15s"
                            _hover={{ borderColor: ativa ? 'purple.400' : 'purple.200', shadow: 'sm' }}
                            onClick={() => {
                                if (c.service_type === escolhida?.service_type) return;
                                setEscolhida(c);
                                setAdicionais([]);
                                setTotalSemAdicionais(null);
                            }}
                        >
                            <Flex justify="space-between" align="center" gap={3} wrap="wrap">
                                <HStack spacing={3} minW={0}>
                                    <Icon as={ICONE_SERVICO[c.service_type] || FaTruck} color={ativa ? 'purple.500' : 'gray.400'} boxSize={5} />
                                    <VStack align="start" spacing={0} minW={0}>
                                        <HStack spacing={2}>
                                            <Text fontSize="sm" fontWeight="bold" color="gray.800">{c.rotulo}</Text>
                                            {doCheckout && <Badge colorScheme="blue" fontSize="9px">escolha do cliente</Badge>}
                                        </HStack>
                                        <Text fontSize="xs" color="gray.500">
                                            {c.distancia_m ? `${(c.distancia_m / 1000).toFixed(1)} km` : 'distância indisponível'}
                                        </Text>
                                    </VStack>
                                </HStack>

                                <HStack
                                    spacing={4}
                                    w={compacto ? '100%' : undefined}
                                    justify={compacto ? 'space-between' : undefined}
                                >
                                    <VStack align="end" spacing={0}>
                                        <Text fontSize="xs" color="gray.500">custo</Text>
                                        <Text fontSize="md" fontWeight="bold" color="gray.800">{brl(c.total)}</Text>
                                    </VStack>
                                    <VStack align="end" spacing={0} minW="88px">
                                        <Text fontSize="xs" color="gray.500">margem</Text>
                                        <Badge colorScheme={cor} fontSize="11px" px={2} py={0.5} borderRadius="md">
                                            {c.margem >= 0 ? '+' : ''}{brl(c.margem).replace('R$ ', 'R$ ')}
                                        </Badge>
                                    </VStack>
                                </HStack>
                            </Flex>
                        </Box>
                    );
                })}
            </VStack>

            {renderAdicionais()}

            {Object.keys(falhas).length > 0 && (
                <Text fontSize="xs" color="gray.500">
                    Sem preço para: {Object.keys(falhas).join(', ')}
                </Text>
            )}

            {pinSelecionado && (
                <Box borderWidth="1px" borderRadius="md" overflow="hidden">
                    <Flex
                        justify="space-between"
                        align={compacto ? 'flex-start' : 'center'}
                        direction={compacto ? 'column' : 'row'}
                        gap={compacto ? 1 : 3}
                        px={3}
                        py={2}
                        bg="gray.50"
                    >
                        <Text fontSize="xs" fontWeight="bold" color="gray.600">
                            Ponto que a Lalamove entendeu. Confira antes de confirmar.
                        </Text>
                        <ChakraLink
                            href={`https://www.google.com/maps/search/?api=1&query=${pinSelecionado.lat},${pinSelecionado.lng}`}
                            isExternal
                            fontSize="xs"
                            color="purple.600"
                            fontWeight="bold"
                            whiteSpace="nowrap"
                        >
                            Abrir no Google Maps <ExternalLinkIcon boxSize={3} />
                        </ChakraLink>
                    </Flex>
                    <Box as="iframe" src={mapaUrl(pinSelecionado.lat, pinSelecionado.lng)} w="100%" h="200px" border="0" title="Ponto de entrega" />
                </Box>
            )}
        </VStack>
    );

    const destinoResolvido = escolhida?.stops?.[1]?.endereco || null;

    const regraEscolhida = regrasGorjeta ? regrasGorjeta[prioridade ? 'prioridade' : 'normal'] : null;
    const gorjetaInicial = regraEscolhida ? valorGorjeta(escolhida?.total, regraEscolhida, 0) : 0;
    const gorjetaMaxima = regraEscolhida ? tetoGorjeta(escolhida?.total, regraEscolhida) : 0;
    const totalAgora = Number(escolhida?.total || 0) + gorjetaInicial;
    const margemAgora = escolhida?.margem != null ? escolhida.margem - gorjetaInicial : null;
    const margemPior = escolhida?.margem != null ? escolhida.margem - gorjetaMaxima : null;

    const renderConfirmacao = () => (
        <VStack align="stretch" spacing={4}>
            <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                A Lalamove cobra na hora. Depois que o motorista aceitar, ou passados 5 minutos, o valor não volta.
            </Alert>

            {regrasGorjeta && (
                <EscolhaPrioridade
                    regras={regrasGorjeta}
                    original={Number(escolhida?.total || 0)}
                    prioridade={prioridade}
                    onChange={setPrioridade}
                    isDisabled={confirmando}
                />
            )}

            <Box
                borderWidth="2px"
                borderColor={prioridade ? 'orange.200' : 'purple.200'}
                bg={prioridade ? 'orange.50' : 'purple.50'}
                borderRadius="lg"
                p={4}
                textAlign="center"
            >
                <Text fontSize="xs" color={prioridade ? 'orange.700' : 'purple.600'} fontWeight="bold" textTransform="uppercase">Você vai pagar agora</Text>
                <Text fontSize="3xl" fontWeight="bold" color={prioridade ? 'orange.700' : 'purple.700'} lineHeight="1.2">{brl(totalAgora)}</Text>
                <Text fontSize="sm" color={prioridade ? 'orange.700' : 'purple.600'}>
                    {escolhida?.rotulo}
                    {gorjetaInicial > 0 && ` · corrida ${brl(escolhida?.total)} + gorjeta ${brl(gorjetaInicial)}`}
                </Text>
                {gorjetaMaxima > gorjetaInicial && (
                    <Text fontSize="xs" color="gray.600" mt={1}>
                        Se ninguém aceitar, pode chegar a <b>{brl(Number(escolhida?.total || 0) + gorjetaMaxima)}</b>
                    </Text>
                )}
            </Box>

            <VStack align="stretch" spacing={2} fontSize="sm">
                <HStack justify="space-between">
                    <Text color="gray.600">Cliente pagou de frete</Text>
                    <Text fontWeight="bold">{brl(dados?.venda?.frete_cobrado)}</Text>
                </HStack>
                <HStack justify="space-between">
                    <Text color="gray.600">Resultado</Text>
                    <Badge colorScheme={corDaMargem(margemAgora, escolhida?.frete_cobrado)} fontSize="12px" px={2} py={1} borderRadius="md">
                        {margemAgora >= 0 ? '+' : ''}{brl(margemAgora)}
                    </Badge>
                </HStack>
                {margemPior !== null && gorjetaMaxima > gorjetaInicial && (
                    <HStack justify="space-between">
                        <Text color="gray.600">No pior caso</Text>
                        <Badge colorScheme={corDaMargem(margemPior, escolhida?.frete_cobrado)} variant="outline" fontSize="12px" px={2} py={1} borderRadius="md">
                            {margemPior >= 0 ? '+' : ''}{brl(margemPior)}
                        </Badge>
                    </HStack>
                )}
                {adicionais.length > 0 && (
                    <>
                        <Divider />
                        <HStack justify="space-between" align="start">
                            <Text color="gray.600" flexShrink={0}>Adicionais</Text>
                            <VStack align="end" spacing={0} maxW="70%">
                                {adicionais.map((a) => (
                                    <Text key={a} fontSize="sm" textAlign="right">{rotuloDoAdicional(a)}</Text>
                                ))}
                                {diferencaAdicionais !== 0 && (
                                    <Text fontSize="xs" color="gray.500">
                                        somaram {brl(diferencaAdicionais)} ao preço
                                    </Text>
                                )}
                            </VStack>
                        </HStack>
                    </>
                )}

                <Divider />
                <HStack justify="space-between" align="start">
                    <Text color="gray.600" flexShrink={0}>Destino</Text>
                    <Text textAlign="right" maxW="70%" fontWeight="medium">
                        {destinoResolvido || linhaLalamove(destino)}
                    </Text>
                </HStack>
                {observacaoDoMotorista(destino) && (
                    <HStack justify="space-between" align="start">
                        <Text color="gray.600" flexShrink={0}>O motoboy lê</Text>
                        <Text textAlign="right" maxW="70%" fontSize="xs" color="gray.600">{observacaoDoMotorista(destino)}</Text>
                    </HStack>
                )}
                {escolhida?.distancia_m && (
                    <HStack justify="space-between" align="start">
                        <Text color="gray.600" flexShrink={0}>Distância</Text>
                        <Text textAlign="right" color="gray.500" fontSize="xs">
                            {(escolhida.distancia_m / 1000).toFixed(1)} km
                        </Text>
                    </HStack>
                )}
            </VStack>

            <Alert status="info" borderRadius="md" fontSize="xs">
                <AlertIcon />
                Este é o endereço que a Lalamove guardou nesta cotação e que o motoboy vai ler. Confira antes de confirmar.
            </Alert>
        </VStack>
    );

    const descartarPendente = async () => {
        setDescartando(true);
        try {
            await descartarPendenteLalamove(alvo);
            toast({ title: 'Solicitação descartada', status: 'success', isClosable: true });
            setConfirmarNaLalamove(false);
            setOrdem(null);
            carregar();
        } catch (e) {
            toast({ title: 'Não foi possível descartar', description: e.response?.data?.error, status: 'error', isClosable: true });
        } finally {
            setDescartando(false);
        }
    };

    const renderPendente = () => (
        <VStack align="stretch" spacing={4}>
            <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <Box>
                    <Text fontWeight="bold">A Lalamove não confirmou esta solicitação.</Text>
                    <Text>
                        A corrida pode ter sido criada mesmo assim. Abra o painel da Lalamove e confira antes de
                        pedir de novo, para não pagar duas vezes.
                    </Text>
                </Box>
            </Alert>

            {ordem?.erro_criacao && (
                <Text fontSize="xs" color="gray.500">Erro registrado: {ordem.erro_criacao}</Text>
            )}

            <Button
                colorScheme="orange"
                variant="outline"
                onClick={descartarPendente}
                isLoading={descartando}
            >
                Conferi, a corrida não foi criada
            </Button>
        </VStack>
    );

    const renderCriada = () => (
        <VStack align="stretch" spacing={4}>
            <Box borderWidth="1px" borderRadius="lg" p={4} bg={ordem?.status === 'COMPLETED' ? 'green.50' : 'gray.50'}>
                <HStack justify="space-between" mb={2} wrap="wrap" spacing={2}>
                    <HStack spacing={2}>
                        <Icon as={ordem?.status === 'COMPLETED' ? FaCheckCircle : FaClipboardCheck} color={`${CORES_STATUS[ordem?.status] || 'gray'}.500`} />
                        <Text fontWeight="bold" fontSize="sm">{ordem?.status_rotulo}</Text>
                        {ordem?.adicionais?.length > 0 && (
                            <Badge colorScheme="purple" variant="subtle" fontSize="9px">
                                {ordem.adicionais.length} adicional{ordem.adicionais.length > 1 ? 'is' : ''}
                            </Badge>
                        )}
                    </HStack>
                    <HStack spacing={2}>
                        <Badge colorScheme="purple" variant="subtle">{ordem?.rotulo}</Badge>
                        <Tooltip label="Atualizar status">
                            <IconButton aria-label="Atualizar" icon={<RepeatIcon />} size="sm" variant="ghost" onClick={atualizarOrdem} isLoading={atualizando} />
                        </Tooltip>
                    </HStack>
                </HStack>

                <HStack spacing={6} wrap="wrap">
                    <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.500">Custo</Text>
                        <Text fontSize="sm" fontWeight="bold">{brl(ordem?.preco_total)}</Text>
                        {ordem?.priority_fee > 0 && (
                            <Text fontSize="10px" color="gray.500">inclui gorjeta {brl(ordem.priority_fee)}</Text>
                        )}
                    </VStack>
                    <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.500">Cliente pagou</Text>
                        <Text fontSize="sm" fontWeight="bold">{brl(ordem?.frete_cobrado)}</Text>
                    </VStack>
                    <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.500">Margem</Text>
                        <Badge colorScheme={corDaMargem(ordem?.margem, ordem?.frete_cobrado)} fontSize="11px" px={2} borderRadius="md">
                            {ordem?.margem >= 0 ? '+' : ''}{brl(ordem?.margem)}
                        </Badge>
                    </VStack>
                </HStack>
            </Box>

            {ordem?.gorjeta && (
                <PainelGorjeta
                    ordem={ordem}
                    onPausar={pausarGorjeta}
                    pausando={pausandoGorjeta}
                    onAumentar={aumentarGorjetaAgora}
                />
            )}

            {(ordem?.motorista?.nome || ordem?.motorista?.telefone) && (
                <Box borderWidth="1px" borderRadius="lg" p={3}>
                    <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={2}>Motorista</Text>
                    <HStack spacing={5} wrap="wrap" fontSize="sm">
                        <HStack spacing={2}><Icon as={FaUser} color="gray.400" boxSize={3} /><Text>{ordem.motorista.nome || '-'}</Text></HStack>
                        <HStack spacing={2}><Icon as={FaPhone} color="gray.400" boxSize={3} /><Text>{ordem.motorista.telefone || '-'}</Text></HStack>
                        {ordem.motorista.placa && <Badge colorScheme="gray">{ordem.motorista.placa}</Badge>}
                    </HStack>
                </Box>
            )}

            {ordem?.share_link && (
                <Button as={ChakraLink} href={ordem.share_link} isExternal colorScheme="purple" variant="outline" size="sm" rightIcon={<ExternalLinkIcon />}>
                    Acompanhar a corrida ao vivo
                </Button>
            )}

            {ordem?.pod_imagem && (
                <ChakraLink href={ordem.pod_imagem} isExternal fontSize="sm" color="purple.600" fontWeight="bold">
                    Ver comprovante de entrega <ExternalLinkIcon boxSize={3} />
                </ChakraLink>
            )}

            {ordem?.motivo_cancelamento && (
                <Text fontSize="xs" color="gray.500">Motivo do cancelamento: {ordem.motivo_cancelamento}</Text>
            )}
        </VStack>
    );

    const renderCorpo = () => {
        if (etapa === 'carregando') {
            return (
                <VStack spacing={3} py={6}>
                    <Spinner color="purple.500" />
                    <Text fontSize="sm" color="gray.500">Carregando dados da venda...</Text>
                    <Skeleton height="60px" w="100%" borderRadius="md" />
                    <Skeleton height="60px" w="100%" borderRadius="md" />
                </VStack>
            );
        }

        if (etapa === 'erro') {
            return (
                <Alert status="error" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    {erro}
                </Alert>
            );
        }

        if (etapa === 'origem') return renderOrigem();
        if (etapa === 'revisao') return renderRevisao();
        if (etapa === 'cotacoes') return renderCotacoes();
        if (etapa === 'confirmacao') return renderConfirmacao();
        if (etapa === 'criada') return confirmarNaLalamove ? renderPendente() : renderCriada();

        return null;
    };

    const renderRodape = () => {
        if (etapa === 'erro' && avulsa) {
            return (
                <HStack w="100%" justify="space-between">
                    <Button variant="ghost" size="sm" onClick={voltarParaOrigem}>Escolher outra origem</Button>
                    <Button size="sm" onClick={onClose}>Fechar</Button>
                </HStack>
            );
        }

        if (etapa === 'origem') {
            return (
                <HStack w="100%" justify="flex-end">
                    <Button variant="ghost" size="sm" onClick={onClose}>Fechar</Button>
                </HStack>
            );
        }

        if (etapa === 'revisao') {
            return (
                <Flex w="100%" justify="space-between" align="center" gap={2} wrap="wrap">
                    <Text fontSize="xs" color={enderecoPronto && destinatarioValido ? 'green.600' : 'orange.600'} flex={1} minW="180px">
                        {!enderecoPronto
                            ? impedimentos[0]
                            : (destinatarioValido ? 'Endereço conferido' : 'Falta o nome e o telefone de quem recebe.')}
                    </Text>
                    <HStack spacing={2}>
                        {avulsa
                            ? <Button variant="ghost" size="sm" onClick={voltarParaOrigem}>Voltar</Button>
                            : <Button variant="ghost" size="sm" onClick={onClose}>Fechar</Button>}
                        <Tooltip label={enderecoPronto ? '' : impedimentos[0]} isDisabled={enderecoPronto} hasArrow>
                            <Button
                                colorScheme="purple"
                                size="sm"
                                onClick={cotar}
                                isLoading={cotando}
                                loadingText="Cotando"
                                isDisabled={!enderecoPronto || !destinatarioValido}
                            >
                                Cotar modalidades
                            </Button>
                        </Tooltip>
                    </HStack>
                </Flex>
            );
        }

        if (etapa === 'cotacoes') {
            return (
                <HStack w="100%" justify="space-between" spacing={2} wrap="wrap">
                    <Button variant="ghost" size="sm" onClick={() => setEtapa('revisao')}>Voltar</Button>
                    <HStack spacing={2}>
                        {nfePendente && (
                            <HStack spacing={1} color="orange.600">
                                <WarningTwoIcon boxSize={3} />
                                <Text fontSize="xs" fontWeight="bold">Emita a NFe para confirmar</Text>
                            </HStack>
                        )}
                        <Button
                            colorScheme="purple"
                            size="sm"
                            onClick={() => setEtapa('confirmacao')}
                            isDisabled={!escolhida || expirada || nfePendente || !enderecoPronto || !destinatarioValido}
                        >
                            Continuar
                        </Button>
                    </HStack>
                </HStack>
            );
        }

        if (etapa === 'confirmacao') {
            return (
                <HStack w="100%" justify="space-between" spacing={2}>
                    <Button variant="ghost" size="sm" onClick={() => setEtapa('cotacoes')} isDisabled={confirmando}>Voltar</Button>
                    <Button
                        colorScheme={prioridade ? 'orange' : 'purple'}
                        size="sm"
                        onClick={confirmar}
                        isLoading={confirmando}
                        loadingText="Criando corrida"
                        flex={{ base: 1, sm: 'initial' }}
                        minH="40px"
                    >
                        {prioridade ? 'Pedir com prioridade' : 'Confirmar e pagar'} {brl(totalAgora)}
                    </Button>
                </HStack>
            );
        }

        if (etapa === 'criada') {
            if (confirmarNaLalamove) {
                return (
                    <HStack w="100%" justify="flex-end" spacing={2}>
                        <Button variant="ghost" size="sm" onClick={onClose}>Fechar</Button>
                    </HStack>
                );
            }

            return (
                <Flex w="100%" justify="space-between" gap={2} wrap="wrap" align="center">
                    <HStack spacing={2} wrap="wrap">
                        {ordem?.cancelavel && (
                            <Button
                                size="sm"
                                colorScheme="red"
                                variant="outline"
                                leftIcon={<Icon as={FaTimesCircle} />}
                                onClick={cancelar}
                                isLoading={cancelando}
                            >
                                Cancelar corrida
                            </Button>
                        )}
                        {ordem?.pode_trocar_motorista && (
                            <Button
                                size="sm"
                                colorScheme="orange"
                                variant="outline"
                                leftIcon={<Icon as={FaMotorcycle} />}
                                onClick={() => setConfirmarTroca(true)}
                                isLoading={trocandoMotorista}
                            >
                                Trocar motorista
                            </Button>
                        )}
                    </HStack>
                    <Button size="sm" onClick={onClose}>Fechar</Button>
                </Flex>
            );
        }

        return <Button size="sm" onClick={onClose}>Fechar</Button>;
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size={compacto ? 'full' : '2xl'} scrollBehavior="inside" closeOnOverlayClick={!confirmando}>
                <ModalOverlay />
                <ModalContent
                    containerProps={compacto ? { height: alturaModal, maxHeight: alturaModal } : undefined}
                    height={compacto ? alturaModal : undefined}
                    maxHeight={compacto ? alturaModal : undefined}
                >
                    {cabecalho}
                    <ModalCloseButton />
                    <ModalBody py={4} ref={corpoRef}>{renderCorpo()}</ModalBody>
                    <ModalFooter borderTopWidth="1px" py={3}>{renderRodape()}</ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={confirmarTroca} onClose={() => setConfirmarTroca(false)} size={compacto ? 'full' : 'md'} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md">Trocar o motorista?</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack align="stretch" spacing={3}>
                            <Text fontSize="sm" color="gray.600">
                                O motorista atual{ordem?.driver_nome ? ` (${ordem.driver_nome})` : ''} vai ser dispensado e a
                                Lalamove vai procurar outro. A corrida continua paga e o preco nao muda.
                            </Text>
                            <FormControl>
                                <FormLabel fontSize="sm" fontWeight="bold" mb={1}>Motivo</FormLabel>
                                <Select size="sm" bg="white" value={motivoTroca} onChange={(e) => setMotivoTroca(e.target.value)}>
                                    {Object.entries(ordem?.motivos_troca_motorista || {}).map(([chave, rotulo]) => (
                                        <option key={chave} value={chave}>{rotulo}</option>
                                    ))}
                                </Select>
                            </FormControl>
                            <Alert status="info" borderRadius="md" fontSize="xs">
                                <AlertIcon />
                                A Lalamove so aceita a troca depois de 15 minutos procurando motorista e antes da coleta.
                            </Alert>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <HStack spacing={2}>
                            <Button size="sm" variant="ghost" onClick={() => setConfirmarTroca(false)} isDisabled={trocandoMotorista}>
                                Voltar
                            </Button>
                            <Button size="sm" colorScheme="orange" onClick={trocarMotorista} isLoading={trocandoMotorista} loadingText="Trocando">
                                Dispensar e procurar outro
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}
